/**
 * /settings/block-lists — manage workspace block list. Two sections:
 *   - PSID blocks: per-IG-account, mute one user from auto-engagement
 *   - Keyword blocks: workspace-wide substring filter on inbound text
 *
 * Worker enforcement is in apps/workers/src/handlers/is-blocked.ts —
 * blocked inbound still gets stored (audit trail) but no triggers
 * fire and no flows start.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface IgAccountRow {
  id: string;
  igUsername: string | null;
  status: string;
}

interface BlockEntry {
  id: string;
  kind: "psid" | "keyword";
  igAccountId: string | null;
  pattern: string;
  reason: string | null;
  createdAt: string;
}

interface WorkspaceSettings {
  id: string;
  profanityFilterEnabled: boolean;
}

export const Route = createFileRoute("/_app/settings/block-lists")({
  component: BlockListsPage,
});

function BlockListsPage() {
  const { activeIgAccount } = useAuth();
  const [accounts, setAccounts] = useState<IgAccountRow[]>([]);
  const [entries, setEntries] = useState<BlockEntry[]>([]);
  const [profanityOn, setProfanityOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // PSID form
  const [psidAccount, setPsidAccount] = useState<string>("");
  const [psid, setPsid] = useState("");
  const [psidReason, setPsidReason] = useState("");

  // Keyword form
  const [keyword, setKeyword] = useState("");
  const [keywordReason, setKeywordReason] = useState("");

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeIgAccount) return;
    void load();
  }, [activeIgAccount?.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const listParams = new URLSearchParams({ ig_account_id: activeIgAccount!.id });
      const [accs, list, settings] = await Promise.all([
        api.get<{ accounts: IgAccountRow[] }>("/ig/accounts"),
        api.get<{ entries: BlockEntry[] }>(`/block-lists?${listParams}`),
        api.get<{ workspace: WorkspaceSettings }>("/workspaces/me/settings"),
      ]);
      setAccounts(accs.data.accounts);
      setEntries(list.data.entries);
      setProfanityOn(Boolean(settings.data.workspace?.profanityFilterEnabled));
      // Pre-select first account so the form's submit button isn't
      // disabled with a confusing "no account" state on workspaces
      // with exactly one account.
      if (!psidAccount && accs.data.accounts[0]) {
        setPsidAccount(accs.data.accounts[0].id);
      }
    } catch {
      setError("Failed to load");
    } finally {
      setLoading(false);
    }
  }

  const psidEntries = useMemo(() => entries.filter((e) => e.kind === "psid"), [entries]);
  const keywordEntries = useMemo(
    () => entries.filter((e) => e.kind === "keyword"),
    [entries],
  );

  async function addPsid(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!psidAccount || !psid.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/block-lists", {
        kind: "psid",
        igAccountId: psidAccount,
        pattern: psid.trim(),
        reason: psidReason.trim() || null,
      });
      setPsid("");
      setPsidReason("");
      await load();
    } catch (err) {
      setError(extractError(err) ?? "Failed to add PSID block");
    } finally {
      setBusy(false);
    }
  }

  async function addKeyword(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!keyword.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/block-lists", {
        kind: "keyword",
        pattern: keyword.trim(),
        reason: keywordReason.trim() || null,
      });
      setKeyword("");
      setKeywordReason("");
      await load();
    } catch (err) {
      setError(extractError(err) ?? "Failed to add keyword block");
    } finally {
      setBusy(false);
    }
  }

  async function toggleProfanity(next: boolean) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch("/workspaces/me/settings", {
        profanityFilterEnabled: next,
      });
      setProfanityOn(next);
    } catch (err) {
      setError(extractError(err) ?? "Failed to update profanity filter");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Remove this block?")) return;
    try {
      await api.delete(`/block-lists/${id}`);
      setEntries((cur) => cur.filter((e) => e.id !== id));
    } catch {
      setError("Failed to delete");
    }
  }

  function accountLabel(id: string | null): string {
    if (!id) return "—";
    const acc = accounts.find((a) => a.id === id);
    return acc ? `@${acc.igUsername ?? acc.id.slice(0, 6)}` : id.slice(0, 8);
  }

  return (
    <section style={{ maxWidth: 800, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>Block list</h1>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Mute spammers and filter junk inbound. Blocked messages still
        appear in the inbox under the &ldquo;Blocked&rdquo; filter for
        review, but the bot won&rsquo;t reply, run flows, or fire
        triggers.
      </p>

      {error && (
        <div
          className="brut-card"
          style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}
        >
          {error}
        </div>
      )}

      <h2 style={{ fontSize: 18, marginBottom: 8 }}>Block by PSID</h2>
      <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0 }}>
        Per-account. The PSID is the long numeric ID Meta gives a contact
        on a specific IG account — copy it from a conversation&rsquo;s
        details panel.
      </p>
      <form
        onSubmit={addPsid}
        className="brut-card"
        style={{ marginBottom: 16, display: "grid", gap: 10 }}
      >
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "200px 1fr" }}>
          <select
            value={psidAccount}
            onChange={(e) => setPsidAccount(e.target.value)}
            className="brut-input"
            required
          >
            <option value="">— IG account —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                @{a.igUsername ?? a.id.slice(0, 8)}
              </option>
            ))}
          </select>
          <input
            placeholder="PSID (e.g. 17841401234567890)"
            value={psid}
            onChange={(e) => setPsid(e.target.value)}
            className="brut-input"
            maxLength={64}
            required
          />
        </div>
        <input
          placeholder="Reason (optional, e.g. spam)"
          value={psidReason}
          onChange={(e) => setPsidReason(e.target.value)}
          className="brut-input"
          maxLength={280}
        />
        <div>
          <button
            type="submit"
            disabled={!psidAccount || !psid.trim() || busy}
            className="brut-button"
          >
            {busy ? "Saving…" : "Add PSID block"}
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading…</p>
      ) : psidEntries.length === 0 ? (
        <div
          className="brut-card"
          style={{ textAlign: "center", padding: 16, marginBottom: 32 }}
        >
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            No PSID blocks yet.
          </p>
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "0 0 32px",
            display: "grid",
            gap: 8,
          }}
        >
          {psidEntries.map((e) => (
            <li
              key={e.id}
              className="brut-card"
              style={{
                padding: 12,
                display: "flex",
                gap: 10,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{e.pattern}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {accountLabel(e.igAccountId)}
                  {e.reason ? ` · ${e.reason}` : ""}
                </div>
              </div>
              <button
                onClick={() => void remove(e.id)}
                className="brut-button is-danger"
                style={{ fontSize: 13, padding: "6px 10px" }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <h2 style={{ fontSize: 18, marginBottom: 8 }}>Profanity filter</h2>
      <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0 }}>
        When ON, inbound text matching a built-in slur/explicit list is
        treated like a keyword block. The list is intentionally small —
        if you need more, add specific keyword blocks below.
      </p>
      <div
        className="brut-card"
        style={{
          marginBottom: 32,
          padding: 12,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={profanityOn}
            disabled={busy || loading}
            onChange={(e) => void toggleProfanity(e.target.checked)}
          />
          <span style={{ fontWeight: 700 }}>
            {profanityOn ? "Enabled" : "Disabled"}
          </span>
        </label>
      </div>

      <h2 style={{ fontSize: 18, marginBottom: 8 }}>Block by keyword</h2>
      <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0 }}>
        Workspace-wide substring match (case-insensitive) on inbound
        text. Use sparingly — overly broad keywords will silently
        suppress automation across all your accounts.
      </p>
      <form
        onSubmit={addKeyword}
        className="brut-card"
        style={{ marginBottom: 16, display: "grid", gap: 10 }}
      >
        <input
          placeholder="Keyword or phrase"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="brut-input"
          maxLength={200}
          required
        />
        <input
          placeholder="Reason (optional)"
          value={keywordReason}
          onChange={(e) => setKeywordReason(e.target.value)}
          className="brut-input"
          maxLength={280}
        />
        <div>
          <button
            type="submit"
            disabled={!keyword.trim() || busy}
            className="brut-button"
          >
            {busy ? "Saving…" : "Add keyword block"}
          </button>
        </div>
      </form>

      {loading ? null : keywordEntries.length === 0 ? (
        <div className="brut-card" style={{ textAlign: "center", padding: 16 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--muted)" }}>
            No keyword blocks yet.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
          {keywordEntries.map((e) => (
            <li
              key={e.id}
              className="brut-card"
              style={{
                padding: 12,
                display: "flex",
                gap: 10,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>&ldquo;{e.pattern}&rdquo;</div>
                {e.reason && (
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>
                    {e.reason}
                  </div>
                )}
              </div>
              <button
                onClick={() => void remove(e.id)}
                className="brut-button is-danger"
                style={{ fontSize: 13, padding: "6px 10px" }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function extractError(err: unknown): string | null {
  const data = (err as { response?: { data?: { message?: string } } }).response?.data;
  return data?.message ?? null;
}
